import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface DropdownContextType {
  openDropdownId: string | null;
  setOpenDropdown: (id: string | null) => void;
  isDropdownOpen: (id: string) => boolean;
}

const DropdownContext = createContext<DropdownContextType | undefined>(undefined);

export const DropdownProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const setOpenDropdown = useCallback((id: string | null) => {
    setOpenDropdownId(id);
  }, []);

  const isDropdownOpen = useCallback(
    (id: string) => {
      return openDropdownId === id;
    },
    [openDropdownId]
  );

  return (
    <DropdownContext.Provider value={{ openDropdownId, setOpenDropdown, isDropdownOpen }}>
      {children}
    </DropdownContext.Provider>
  );
};

export const useDropdown = () => {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error('useDropdown must be used within a DropdownProvider');
  }
  return context;
};

// Hook to manage a specific dropdown
export const useDropdownControl = (dropdownId: string) => {
  const { isDropdownOpen, setOpenDropdown } = useDropdown();

  const isOpen = isDropdownOpen(dropdownId);

  const onToggle = useCallback(
    (nextShow: boolean) => {
      if (nextShow) {
        setOpenDropdown(dropdownId);
      } else {
        setOpenDropdown(null);
      }
    },
    [dropdownId, setOpenDropdown]
  );

  return { isOpen, onToggle };
};
